import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { UserRole } from "reedy-data/models";
import startCase from "lodash/startCase";
import debounce from "lodash/debounce";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectPlaceholder,
  SelectTrigger,
} from "~/components/ui/select";
import { useOrganisation } from "~/contexts/organisation-provider";
import { toast } from "~/utils/toast";
import { IUser } from "~/integrations/firebase/collections";
import { logError } from "~/shared/services/error-reporting";
import { TransferUsersConfirmationDialog } from "./transfer-users-confirmation-dialog";

interface Member {
  email: string;
  role: UserRole;
}

interface FormValues {
  users: Member[];
}

interface AddOrganisationMemberFormProps {
  onClose(): void;
  onSubmitting(isSubmitting: boolean): void;
  onValidationErrors(hasValidationErrors: boolean): void;
}

export const AddOrganisationMemberForm = ({
  onClose,
  onSubmitting,
  onValidationErrors,
}: AddOrganisationMemberFormProps) => {
  const { t } = useTranslation(["settings"]);
  const [existingUsersToConfirm, setExistingUsersToConfirm] = useState<IUser[]>(
    [],
  );
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailErrors, setEmailErrors] = useState<Record<number, string>>({});

  const { addUsers, getUsersByEmail, transferUser, organisation } =
    useOrganisation();

  const { formState, handleSubmit, register, control } = useForm<FormValues>({
    defaultValues: {
      users: [
        {
          email: "",
          role: UserRole.MEMBER,
        },
      ],
    },
  });

  // Determine if the form has any validation errors
  const hasValidationErrors = Object.keys(emailErrors).length > 0;
  useEffect(() => {
    onValidationErrors(hasValidationErrors);
  }, [hasValidationErrors, onValidationErrors]);

  const debouncedValidate = useCallback(
    (email: string, index: number) => {
      if (!email.trim() || !organisation) return;

      const validateAsync = async () => {
        try {
          const users = await getUsersByEmail([email]);
          const existingUser = users.find(
            (user) =>
              user.email === email && user.organisation?.id === organisation.id,
          );

          if (existingUser) {
            setEmailErrors((prev) => ({
              ...prev,
              [index]: t("settings:organisation.members.add.alreadyMember"),
            }));
          } else {
            setEmailErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[index];
              return newErrors;
            });
          }
        } catch (error) {
          logError("Error validating emails:", error);
        }
      };

      const debouncedFn = debounce(validateAsync, 500);
      debouncedFn();

      return () => debouncedFn.cancel();
    },
    [organisation, getUsersByEmail, t],
  );

  const validateEmail = (email: string, index: number) => {
    if (!email.trim()) {
      setEmailErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return;
    }

    debouncedValidate(email, index);
  };

  const updateSubmissionState = (submitting: boolean) => {
    setIsSubmitting(submitting);
    onSubmitting(submitting);
  };

  async function getExistingUsers(emails: string[]) {
    const users = await getUsersByEmail(emails);
    const existingUsers = users.filter((user) => user.organisation?.id);
    return existingUsers;
  }

  async function transferExistingUsers(users: IUser[]) {
    if (!organisation) {
      return [];
    }
    const errors: unknown[] = [];
    const promises = users.map((user) =>
      transferUser(user.organisation.id, organisation.id, user.id).catch((e) =>
        errors.push(e),
      ),
    );

    await Promise.all(promises);
    return errors;
  }

  async function confirmTransferUsers() {
    if (!formValues || !existingUsersToConfirm.length) return;

    updateSubmissionState(true);
    try {
      const transferErrors = await transferExistingUsers(
        existingUsersToConfirm,
      );

      // Add any new users that weren't in the existing users list
      const existingEmails = existingUsersToConfirm.map((user) => user.email);
      const newUsers = formValues.users.filter(
        (user) => !existingEmails.includes(user.email),
      );

      if (newUsers.length > 0) {
        await addUsers(newUsers);
      }

      if (transferErrors.length) {
        logError(transferErrors);
        toast.error(t("settings:organisation.members.add.transferError"));
      } else {
        toast.success(t("settings:organisation.members.add.success"));
        onClose();
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      updateSubmissionState(false);
      setExistingUsersToConfirm([]);
      setFormValues(null);
    }
  }

  function cancelTransferUsers() {
    setExistingUsersToConfirm([]);
    setFormValues(null);
  }

  async function onCreate(values: FormValues) {
    if (formState.isSubmitting) {
      return;
    }

    // Check if there are any validation errors
    if (hasValidationErrors) {
      toast.error(t("settings:organisation.members.add.validationError"));
      return;
    }

    updateSubmissionState(true);
    try {
      const existingUsers = await getExistingUsers(
        values.users.map((user) => user.email),
      );
      if (!existingUsers.length) {
        await addUsers(values.users);
        toast.success(t("settings:organisation.members.add.success"));
        onClose();
      } else {
        setExistingUsersToConfirm(existingUsers);
        setFormValues(values);
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      if (!existingUsersToConfirm.length) {
        updateSubmissionState(false);
      }
    }
  }

  const members = useFieldArray({
    control,
    name: "users",
  });

  const handleAddMember = () => {
    members.append({
      email: "",
      role: UserRole.MEMBER,
    });
  };

  const handleRemoveMember = (index: number) => {
    members.remove(index);

    // Update errors after removal
    setEmailErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];

      // Adjust indices for errors after the removed item
      const adjustedErrors: Record<number, string> = {};
      Object.entries(newErrors).forEach(([key, value]) => {
        const keyNum = parseInt(key, 10);
        if (keyNum > index) {
          adjustedErrors[keyNum - 1] = value;
        } else {
          adjustedErrors[keyNum] = value;
        }
      });

      return adjustedErrors;
    });
  };

  return (
    <>
      <form
        id="add-organisation-members"
        onSubmit={handleSubmit(onCreate)}
        className="grid gap-3 grid-cols-[1fr_150px_auto]"
      >
        <Text variant="label">
          {t("settings:organisation.members.add.modal.email.label")}
        </Text>
        <Text variant="label">
          {t("settings:organisation.members.add.modal.role.label")}
        </Text>

        {members.fields.map((field, index) => (
          <div
            className="grid grid-cols-subgrid gap-3 col-span-full"
            key={field.id}
          >
            <div className="flex flex-col ">
              <Input
                className={`placeholder:text-secondary-400 rounded-lg placeholder:text-sm placeholder:font-normal text-sm h-10 ${
                  emailErrors[index] ? "border-system-error" : ""
                }`}
                placeholder={t(
                  "settings:organisation.members.add.modal.email.placeholder",
                )}
                type="email"
                {...register(`users.${index}.email`, {
                  required: true,
                  setValueAs: (v) => v.trim(),
                  onChange: (e) => validateEmail(e.target.value, index),
                })}
              />
              {emailErrors[index] && (
                <Text as="span" variant="errorSmall" className="mt-1">
                  {emailErrors[index]}
                </Text>
              )}
            </div>

            <div className="flex items-start h-10 w-full">
              <Controller
                name={`users.${index}.role`}
                control={control}
                rules={{ required: true }}
                render={({ field: roleField }) => (
                  <Select
                    onValueChange={roleField.onChange}
                    value={roleField.value}
                  >
                    <SelectTrigger
                      aria-label="category"
                      className="h-full w-full border-secondary-300 bg-white"
                    >
                      <SelectPlaceholder
                        placeholder={t(
                          "settings:organisation.members.add.modal.role.placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent className="w-full">
                      <SelectGroup>
                        {Object.entries(UserRole).map(([key, value]) => (
                          <SelectItem
                            aria-label={key}
                            key={key}
                            value={value}
                            className="text-sm"
                          >
                            {startCase(value.toLowerCase())}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="flex items-start h-10">
              <Button
                icon="trash"
                variant="tertiary"
                size="small"
                className="aspect-square p-3"
                disabled={members.fields.length === 1}
                onClick={() => handleRemoveMember(index)}
              />
            </div>
          </div>
        ))}
      </form>

      <div>
        <Button
          type="button"
          size="small"
          icon="plus"
          variant="tertiary"
          className="p-3"
          onClick={handleAddMember}
        />
      </div>

      <TransferUsersConfirmationDialog
        open={existingUsersToConfirm.length > 0}
        existingUsersToConfirm={existingUsersToConfirm}
        formValues={formValues}
        isSubmitting={isSubmitting}
        onClose={() => cancelTransferUsers()}
        onConfirm={() => confirmTransferUsers()}
      />
    </>
  );
};
